import React, { useState } from 'react';
import { Settings2, Calendar, TrendingUp, Info, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from '../hooks/use-toast';

const DEFAULT_YEARS = ['2025', '2026', '2027'];

const DEFAULT_PRICING = {
  '2025': {
    // Passive Cable prices
    passive28PricePerMeter: 1.3 * 3 / 0.3048,       // $12.795/m
    passive42PricePerMeter: 4.45 * 2.225 / 0.3048,   // $32.485/m
    passive56PricePerMeter: 4.45 * 2.225 / 0.3048,   // $32.485/m
    // Passive connectors
    connectorPrice32Female: 286.0,
    connectorPrice32Male: 286.0,
    connectorPrice55Female: 286.0,
    connectorPrice55Male: 286.0,
    connectorPrice61Female: 398.0,
    connectorPrice61Male: 398.0,
    // Passive electrodes
    ssElectrodePrice: 90.0,
    graphiteElectrodePrice: 206.0,
    // Passive multipliers
    passiveOverheadMultiplier: 1.13,
    passiveProfitMultiplier: 1.30,

    // Dual Mode prices
    activeCablePrice: 2.81 * 5 / 0.3048,  // $46.09/m
    dualConnectorPrice: 125.0,
    dualElectrodePrice: 600.0,
    dualOverheadMultiplier: 1.13,

    // Marine prices
    marineCablePrice: 3.25 * 4.5 / 0.3048, // $47.97/m
    marineConnectorPrice: 286.0,
    marineSSElectrodePrice: 90.0,
    marineGraphiteElectrodePrice: 206.0,
    marineFixedCost: 608.74,
    marineOverheadMultiplier: 1.13,
    marineProfitMultiplier: 1.30,
  },
  '2026': {
    // ~5% increase from 2025
    passive28PricePerMeter: 1.3 * 3 / 0.3048 * 1.05,
    passive42PricePerMeter: 4.45 * 2.225 / 0.3048 * 1.05,
    passive56PricePerMeter: 4.45 * 2.225 / 0.3048 * 1.05,
    connectorPrice32Female: 300.0,
    connectorPrice32Male: 300.0,
    connectorPrice55Female: 300.0,
    connectorPrice55Male: 300.0,
    connectorPrice61Female: 418.0,
    connectorPrice61Male: 418.0,
    ssElectrodePrice: 95.0,
    graphiteElectrodePrice: 216.0,
    passiveOverheadMultiplier: 1.13,
    passiveProfitMultiplier: 1.30,

    activeCablePrice: 2.81 * 5 / 0.3048 * 1.05,
    dualConnectorPrice: 131.0,
    dualElectrodePrice: 630.0,
    dualOverheadMultiplier: 1.13,

    marineCablePrice: 3.25 * 4.5 / 0.3048 * 1.05,
    marineConnectorPrice: 300.0,
    marineSSElectrodePrice: 95.0,
    marineGraphiteElectrodePrice: 216.0,
    marineFixedCost: 639.0,
    marineOverheadMultiplier: 1.13,
    marineProfitMultiplier: 1.30,
  },
  '2027': {
    // ~10% increase from 2025
    passive28PricePerMeter: 1.3 * 3 / 0.3048 * 1.10,
    passive42PricePerMeter: 4.45 * 2.225 / 0.3048 * 1.10,
    passive56PricePerMeter: 4.45 * 2.225 / 0.3048 * 1.10,
    connectorPrice32Female: 315.0,
    connectorPrice32Male: 315.0,
    connectorPrice55Female: 315.0,
    connectorPrice55Male: 315.0,
    connectorPrice61Female: 438.0,
    connectorPrice61Male: 438.0,
    ssElectrodePrice: 99.0,
    graphiteElectrodePrice: 227.0,
    passiveOverheadMultiplier: 1.13,
    passiveProfitMultiplier: 1.30,

    activeCablePrice: 2.81 * 5 / 0.3048 * 1.10,
    dualConnectorPrice: 138.0,
    dualElectrodePrice: 660.0,
    dualOverheadMultiplier: 1.13,

    marineCablePrice: 3.25 * 4.5 / 0.3048 * 1.10,
    marineConnectorPrice: 315.0,
    marineSSElectrodePrice: 99.0,
    marineGraphiteElectrodePrice: 227.0,
    marineFixedCost: 670.0,
    marineOverheadMultiplier: 1.13,
    marineProfitMultiplier: 1.30,
  }
};

const PricingSettings = ({ activeYear, setActiveYear, yearlyPricing, setYearlyPricing }) => {
  const currentPricing = yearlyPricing[activeYear];
  const [pendingYear, setPendingYear] = useState(null);
  const [showAddYear, setShowAddYear] = useState(false);
  const [newYearInput, setNewYearInput] = useState('');

  // Derive years from yearlyPricing keys, sorted
  const YEARS = Object.keys(yearlyPricing).sort();

  const handleYearChange = (year) => {
    if (year !== activeYear) {
      setPendingYear(year);
    }
  };

  const confirmYearChange = () => {
    if (pendingYear) {
      setActiveYear(pendingYear);
      toast({ title: 'Year Changed', description: `Now using ${pendingYear} pricing for all calculators` });
      setPendingYear(null);
    }
  };

  const cancelYearChange = () => {
    setPendingYear(null);
  };

  const addNewYear = () => {
    const year = newYearInput.trim();
    if (!year || !/^\d{4}$/.test(year)) {
      toast({ title: 'Invalid Year', description: 'Please enter a valid 4-digit year', variant: 'destructive' });
      return;
    }
    if (yearlyPricing[year]) {
      toast({ title: 'Year Exists', description: `${year} pricing already exists`, variant: 'destructive' });
      return;
    }
    // Clone from the nearest existing year
    const existingYears = Object.keys(yearlyPricing).map(Number).sort((a, b) => a - b);
    const targetNum = Number(year);
    let closestYear = existingYears[0];
    let minDiff = Math.abs(targetNum - closestYear);
    for (const ey of existingYears) {
      if (Math.abs(targetNum - ey) < minDiff) {
        minDiff = Math.abs(targetNum - ey);
        closestYear = ey;
      }
    }
    setYearlyPricing(prev => ({
      ...prev,
      [year]: { ...prev[String(closestYear)] }
    }));
    setNewYearInput('');
    setShowAddYear(false);
    toast({ title: 'Year Added', description: `${year} pricing created (copied from ${closestYear}). You can now edit its values.` });
  };

  const updateField = (field, value) => {
    setYearlyPricing(prev => ({
      ...prev,
      [activeYear]: {
        ...prev[activeYear],
        [field]: Number(value)
      }
    }));
  };

  const resetYear = (year) => {
    if (DEFAULT_PRICING[year]) {
      setYearlyPricing(prev => ({
        ...prev,
        [year]: { ...DEFAULT_PRICING[year] }
      }));
      toast({ title: 'Reset Complete', description: `${year} pricing reset to defaults` });
    } else {
      toast({ title: 'No Defaults', description: `${year} has no default pricing to reset to`, variant: 'destructive' });
    }
  };

  const resetAll = () => {
    setYearlyPricing({ ...DEFAULT_PRICING });
    toast({ title: 'Reset Complete', description: 'All yearly pricing reset to defaults' });
  };

  // Compute year-over-year change for a given field
  const getYoYChange = (field, year) => {
    const prevYear = String(Number(year) - 1);
    if (!yearlyPricing[prevYear]) return null;
    const current = yearlyPricing[year][field];
    const previous = yearlyPricing[prevYear][field];
    if (!previous) return null;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const PriceField = ({ label, field, prefix = '$' }) => {
    const yoy = getYoYChange(field, activeYear);
    return (
      <div className="space-y-1">
        <Label className="text-xs text-slate-600">{label}</Label>
        <div className="relative">
          <Input
            type="number"
            step="0.01"
            value={currentPricing[field]}
            onChange={(e) => updateField(field, e.target.value)}
            className="border-slate-300 pr-16"
          />
          {yoy !== null && (
            <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium ${
              Number(yoy) > 0 ? 'text-red-500' : Number(yoy) < 0 ? 'text-green-500' : 'text-slate-400'
            }`}>
              {Number(yoy) > 0 ? '+' : ''}{yoy}%
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-amber-900">
              <strong>Yearly Pricing Settings:</strong> Set manufacturing costs for each year. The selected year's
              prices are used across all cable calculators. This lets you track how costs change over time and
              project future pricing.
            </p>
          </div>
        </div>
      </div>

      {/* Year Change Confirmation Dialog */}
      <AlertDialog open={!!pendingYear} onOpenChange={(open) => { if (!open) cancelYearChange(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Pricing Year?</AlertDialogTitle>
            <AlertDialogDescription>
              Switching from <strong>{activeYear}</strong> to <strong>{pendingYear}</strong> will update all calculator
              prices to use {pendingYear} pricing. Any unsaved quotes will reflect the new year's costs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelYearChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmYearChange} className="bg-amber-600 hover:bg-amber-700">
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Year Selector */}
      <Card className="border-slate-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Calendar className="w-5 h-5 text-amber-600" />
            Active Price Year
          </CardTitle>
          <CardDescription>Select which year's pricing to use for all calculations</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 flex-wrap">
            <Label className="text-slate-700 font-medium whitespace-nowrap">Pricing Year:</Label>
            <Select value={activeYear} onValueChange={handleYearChange}>
              <SelectTrigger className="w-40 border-amber-300 bg-amber-50 font-semibold text-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-green-700 font-medium">Active: {activeYear} prices used in all calculators</span>
            </div>
            {!showAddYear ? (
              <Button
                onClick={() => setShowAddYear(true)}
                variant="outline"
                size="sm"
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Year
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="e.g. 2028"
                  value={newYearInput}
                  onChange={(e) => setNewYearInput(e.target.value)}
                  className="w-28 border-amber-300"
                  maxLength={4}
                />
                <Button onClick={addNewYear} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                  Add
                </Button>
                <Button onClick={() => { setShowAddYear(false); setNewYearInput(''); }} variant="ghost" size="sm">
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Year-over-Year Comparison Summary */}
      <Card className="border-indigo-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-200">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            Cost Comparison Across Years
          </CardTitle>
          <CardDescription>Manufacturing cost trends (cable price per meter)</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-indigo-200">
                  <th className="text-left py-2 px-3 text-slate-700">Component</th>
                  {YEARS.map(year => (
                    <th key={year} className={`text-right py-2 px-3 ${year === activeYear ? 'text-indigo-700 font-bold bg-indigo-50 rounded' : 'text-slate-600'}`}>
                      {year} {year === activeYear && '✓'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: '28 Cond. Passive ($/m)', field: 'passive28PricePerMeter' },
                  { label: '42 Cond. Passive ($/m)', field: 'passive42PricePerMeter' },
                  { label: 'Active Dual Mode ($/m)', field: 'activeCablePrice' },
                  { label: 'Marine Cable ($/m)', field: 'marineCablePrice' },
                  { label: 'SS Electrode ($)', field: 'ssElectrodePrice' },
                  { label: 'Graphite Electrode ($)', field: 'graphiteElectrodePrice' },
                  { label: 'Dual Electrode ($)', field: 'dualElectrodePrice' },
                  { label: '32-pin Connector ($)', field: 'connectorPrice32Female' },
                  { label: '61-pin Connector ($)', field: 'connectorPrice61Female' },
                ].map(row => (
                  <tr key={row.field} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 text-slate-700">{row.label}</td>
                    {YEARS.map(year => (
                      <td key={year} className={`text-right py-2 px-3 font-mono ${year === activeYear ? 'font-bold text-indigo-700 bg-indigo-50' : 'text-slate-600'}`}>
                        ${yearlyPricing[year][row.field].toFixed(2)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Passive Cable Pricing */}
      <Card className="border-blue-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b border-blue-200">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Settings2 className="w-5 h-5 text-blue-600" />
            Passive Cable Pricing — {activeYear}
          </CardTitle>
          <CardDescription>Cable, connector, and electrode costs for passive cables</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Cable Price per Meter ($)</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <PriceField label="28 Conductor" field="passive28PricePerMeter" />
                <PriceField label="42 Conductor" field="passive42PricePerMeter" />
                <PriceField label="56 Conductor" field="passive56PricePerMeter" />
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Connector Prices ($)</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <PriceField label="32-pin Female (M24)" field="connectorPrice32Female" />
                <PriceField label="32-pin Male (M25)" field="connectorPrice32Male" />
                <PriceField label="55-pin Female (M26)" field="connectorPrice55Female" />
                <PriceField label="55-pin Male (M27)" field="connectorPrice55Male" />
                <PriceField label="61-pin Female (M28)" field="connectorPrice61Female" />
                <PriceField label="61-pin Male (M29)" field="connectorPrice61Male" />
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Electrode Prices ($)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <PriceField label="Stainless Steel (M17)" field="ssElectrodePrice" />
                <PriceField label="Graphite (M18)" field="graphiteElectrodePrice" />
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Markup Multipliers</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <PriceField label="Overhead Multiplier (e.g. 1.13 = 13%)" field="passiveOverheadMultiplier" />
                <PriceField label="Profit Multiplier (e.g. 1.30 = 30%)" field="passiveProfitMultiplier" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dual Mode Pricing */}
      <Card className="border-green-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Settings2 className="w-5 h-5 text-green-600" />
            Dual Mode Cable Pricing — {activeYear}
          </CardTitle>
          <CardDescription>SuperSting Active Cable (Dual Mode) costs</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Component Prices ($)</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <PriceField label="Active Cable Price per Meter (O5)" field="activeCablePrice" />
                <PriceField label="Connector Price (M7)" field="dualConnectorPrice" />
                <PriceField label="Electrode Price (M6)" field="dualElectrodePrice" />
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Markup Multipliers</h3>
              <div className="grid md:grid-cols-1 gap-4">
                <PriceField label="Overhead Multiplier (e.g. 1.13 = 13%)" field="dualOverheadMultiplier" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Note: No profit multiplier for Dual Mode (per Excel formula)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Marine Cable Pricing */}
      <Card className="border-cyan-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 border-b border-cyan-200">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Settings2 className="w-5 h-5 text-cyan-600" />
            Marine Cable Pricing — {activeYear}
          </CardTitle>
          <CardDescription>AGI 56 Conductor Marine Cable costs</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Cable & Component Prices ($)</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <PriceField label="Marine Cable Price per Meter (O31)" field="marineCablePrice" />
                <PriceField label="Connector Price (M32)" field="marineConnectorPrice" />
                <PriceField label="SS Electrode (M17)" field="marineSSElectrodePrice" />
                <PriceField label="Graphite Electrode (M18)" field="marineGraphiteElectrodePrice" />
                <PriceField label="Fixed Cost (M29)" field="marineFixedCost" />
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Markup Multipliers</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <PriceField label="Overhead Multiplier (e.g. 1.13 = 13%)" field="marineOverheadMultiplier" />
                <PriceField label="Profit Multiplier (e.g. 1.30 = 30%)" field="marineProfitMultiplier" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reset Buttons */}
      <Card className="border-slate-200 shadow-lg">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => resetYear(activeYear)}
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
            >
              Reset {activeYear} to Defaults
            </Button>
            <Button
              onClick={resetAll}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              Reset All Years to Defaults
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { DEFAULT_PRICING, DEFAULT_YEARS };
export default PricingSettings;
